import { useState, useEffect, useRef } from "react";
import { useAuth, API, authFetch } from "@/App";
import { Plus, Trash2, Save, User as UserIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

/**
 * PerEmployeeTADAEditor
 * Lets admins set per-employee overrides for TADA rate per KM, daily allowance,
 * and food allowance. Falls back to the global Expenses & TADA defaults shown
 * in the parent panel when no override exists. Persists to
 *   PUT /api/admin/tada-overrides   (body: { employee_id, rate_per_km, daily_allowance, food_allowance })
 *   DELETE /api/admin/tada-overrides/{employee_id}
 *   GET /api/admin/tada-overrides
 */
export const PerEmployeeTADAEditor = ({ defaults }) => {
  const [employees, setEmployees] = useState([]);
  const [overrides, setOverrides] = useState([]); // [{employee_id, employee_name, rate_per_km, daily_allowance, food_allowance}]
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({
    employee_id: "",
    rate_per_km: "",
    daily_allowance: "",
    food_allowance: "",
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [empRes, ovrRes] = await Promise.all([
        authFetch(`${API}/employees?limit=500`),
        authFetch(`${API}/admin/tada-overrides`),
      ]);
      const empData = await empRes.json();
      const ovrData = await ovrRes.json();
      setEmployees(empData.data || empData.employees || []);
      setOverrides(ovrData.data || []);
    } catch {
      toast.error("Failed to load TADA overrides");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const saveOverride = async (payload) => {
    if (!payload.employee_id) {
      toast.error("Pick an employee");
      return;
    }
    const body = {
      employee_id: payload.employee_id,
      rate_per_km: parseFloat(payload.rate_per_km) || null,
      daily_allowance: parseFloat(payload.daily_allowance) || null,
      food_allowance: parseFloat(payload.food_allowance) || null,
    };
    const r = await authFetch(`${API}/admin/tada-overrides`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      toast.success("TADA override saved");
      setDraft({ employee_id: "", rate_per_km: "", daily_allowance: "", food_allowance: "" });
      fetchAll();
    } else {
      const d = await r.json();
      toast.error(d.detail || "Failed");
    }
  };

  const removeOverride = async (empId) => {
    if (!window.confirm("Remove TADA override for this employee?")) return;
    const r = await authFetch(`${API}/admin/tada-overrides/${empId}`, { method: "DELETE" });
    if (r.ok) {
      toast.success("Override removed");
      fetchAll();
    }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));

  return (
    <div className="py-2" data-testid="tada-overrides-section">
      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
        Per-Employee TADA Overrides
      </p>
      <p className="text-[10px] text-slate-400 mb-2">
        Allowances vary by employee. Set custom KM rate / daily / food allowance below; missing values
        fall back to the defaults set above.
      </p>

      {loading ? (
        <div className="text-[10px] text-slate-400 py-2">Loading…</div>
      ) : (
        <div className="space-y-1.5 mb-3">
          {overrides.length === 0 ? (
            <div className="text-[10px] text-slate-400 italic px-2 py-3 border border-dashed dark:border-slate-700 rounded text-center">
              No employee-specific overrides yet — all employees use the global defaults
            </div>
          ) : (
            overrides.map((o) => (
              <div
                key={o.employee_id}
                className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700 text-xs"
                data-testid={`tada-override-${o.employee_id}`}
              >
                <UserIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-700 dark:text-slate-200 truncate">
                    {empMap[o.employee_id]?.full_name || empMap[o.employee_id]?.name || o.employee_name || o.employee_id}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    KM: ₹{o.rate_per_km ?? defaults?.tada_rate_per_km ?? "—"} ·
                    {" "}DA: ₹{o.daily_allowance ?? defaults?.daily_allowance ?? "—"} ·
                    {" "}Food: ₹{o.food_allowance ?? defaults?.food_allowance ?? "—"}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-red-500"
                  onClick={() => removeOverride(o.employee_id)}
                  data-testid={`remove-tada-${o.employee_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      <Card className="p-3 dark:bg-slate-900 border-dashed">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <div className="md:col-span-2">
            <label className="text-[10px] text-slate-500 mb-1 block">Employee</label>
            <Select
              value={draft.employee_id}
              onValueChange={(v) => setDraft({ ...draft, employee_id: v })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="tada-override-emp">
                <SelectValue placeholder="Pick employee" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {employees.map((e) => (
                  <SelectItem key={e.employee_id} value={e.employee_id}>
                    {(e.full_name || e.name || e.employee_id) +
                      (e.employee_code ? ` (${e.employee_code})` : "")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 mb-1 block">Rate/KM (₹)</label>
            <Input
              type="number"
              step="0.5"
              placeholder={defaults?.tada_rate_per_km ?? ""}
              value={draft.rate_per_km}
              onChange={(e) => setDraft({ ...draft, rate_per_km: e.target.value })}
              className="h-8 text-xs"
              data-testid="tada-override-rate"
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 mb-1 block">Daily (₹)</label>
            <Input
              type="number"
              placeholder={defaults?.daily_allowance ?? ""}
              value={draft.daily_allowance}
              onChange={(e) => setDraft({ ...draft, daily_allowance: e.target.value })}
              className="h-8 text-xs"
              data-testid="tada-override-daily"
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 mb-1 block">Food (₹)</label>
            <Input
              type="number"
              placeholder={defaults?.food_allowance ?? ""}
              value={draft.food_allowance}
              onChange={(e) => setDraft({ ...draft, food_allowance: e.target.value })}
              className="h-8 text-xs"
              data-testid="tada-override-food"
            />
          </div>
        </div>
        <div className="flex justify-end mt-2">
          <Button
            size="sm"
            onClick={() => saveOverride(draft)}
            className="h-7 px-3 bg-[#8B1A1A] text-white hover:bg-[#6B1515]"
            data-testid="save-tada-override"
          >
            <Plus className="w-3 h-3 mr-1" /> Add / Update Override
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PerEmployeeTADAEditor;
